import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { clearCustomerWidgetAuthToken } from "@redotech/redo-customer-portal-app/api";
import { SetAuthContext } from "@redotech/redo-customer-portal-app/contexts/auth";
import { CustomerContext } from "@redotech/redo-customer-portal-app/contexts/CustomerContext";
import { SettingsContext } from "@redotech/redo-customer-portal-app/contexts/settings";
import {
  SetUserContext,
  UserContext,
} from "@redotech/redo-customer-portal-app/contexts/user";
import { getPrimaryCustomerEmail } from "@redotech/redo-model/customer";
import { Avatar } from "@redotech/redo-web/arbiter-components/avatars/avatar";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoListDropdown } from "@redotech/redo-web/arbiter-components/list/redo-list-dropdown";
import LeftArrow from "@redotech/redo-web/arbiter-icon/arrow-left.svg";
import ChevronDownIcon from "@redotech/redo-web/arbiter-icon/chevron-down_filled.svg";
import { Flex } from "@redotech/redo-web/flex";
import { getGravatarUrlFromEmail } from "@redotech/redo-web/gravatar-utils";
import { Text } from "@redotech/redo-web/text";
import { allowCustomerAccounts } from "@redotech/redo-web/utils/customer-account-utils";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as navbarCss from "./version-four-navbar.module.css";
enum MenuOption {
  MyOrders = "My orders",
  MyAccount = "My account",
  Logout = "Logout",
  MyWarranties = "My warranties",
}

const optionToLabel: Record<MenuOption, string> = {
  [MenuOption.MyOrders]: "My Orders",
  [MenuOption.MyAccount]: "My Account",
  [MenuOption.Logout]: "Logout",
  [MenuOption.MyWarranties]: "My Warranties",
};

export function VersionFourNavbar() {
  const settings = useContext(SettingsContext);
  const customerContext = useContext(CustomerContext);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const user = useContext(UserContext);
  const setUser = useContext(SetUserContext);
  const setAuth_ = useContext(SetAuthContext);
  const [dropdownButtonRef, setDropdownButtonRef] =
    useState<HTMLButtonElement | null>(null);

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const screen = useScreenType();

  const primaryEmail = getPrimaryCustomerEmail(customerContext);
  const customerName = customerContext?.name;

  const menuOptions: RedoListItem<MenuOption>[] = useMemo(() => {
    const myOrdersOption =
      user && !user?.guestEmail && user.email ? [MenuOption.MyOrders] : [];
    const myWarrantiesOption =
      user && !user?.guestEmail && user.email && settings?.warranties?.enabled
        ? [MenuOption.MyWarranties]
        : [];
    const myAccountOption =
      allowCustomerAccounts(settings?.customerAccounts) &&
      !!customerContext &&
      primaryEmail
        ? [MenuOption.MyAccount]
        : [];

    const menuOptions = [
      myOrdersOption,
      myWarrantiesOption,
      myAccountOption,
      MenuOption.Logout,
    ].flat();

    return menuOptions.map((option) => ({
      id: option,
      value: option,
      type: "text",
      text: optionToLabel[option],
    }));
  }, [user, settings, customerContext, primaryEmail]);

  const shopAppLink = params.get("shopAppLink");
  const fromShopMini = shopAppLink !== null;

  const ShopAppBack = memo(function ShopAppBack({ link }: { link: string }) {
    return (
      <Flex
        align="center"
        flexDirection="row"
        justify="center"
        onClick={() => window.open(link)}
      >
        <LeftArrow />
        Shop App
      </Flex>
    );
  });

  return (
    <Flex
      align="center"
      bgColor={screen === ScreenType.DESKTOP ? "primary" : "secondary"}
      borderBottomWidth="1px"
      borderColor="secondary"
      borderStyle="solid"
      className={navbarCss.navBar}
      dir="row"
      justify="space-between"
      position="sticky"
      px={screen === ScreenType.DESKTOP ? "6xl" : "lg"}
      py="lg"
      top="0"
    >
      {fromShopMini ? (
        <ShopAppBack link={shopAppLink} />
      ) : (
        <a
          // @ts-expect-error: strict-null-checks
          href={`https://${settings.storeUrl}`}
          rel="noreferrer"
          target="_blank"
        >
          <img
            alt="Store logo"
            className={navbarCss.navBarLogo}
            id="navbar-logo"
            // @ts-expect-error: strict-null-checks
            src={settings.theme?.logo_image}
          />
        </a>
      )}

      <RedoListDropdown
        dropdownAnchor={dropdownButtonRef}
        dropdownOpen={dropdownOpen}
        items={menuOptions}
        itemSelected={({ item }) => {
          switch (item.value) {
            case MenuOption.MyOrders:
              navigate("/returns-portal/orders");
              break;
            case MenuOption.MyAccount:
              navigate("/returns-portal/account");
              break;
            case MenuOption.Logout:
              // @ts-expect-error: strict-null-checks
              setAuth_(undefined);
              // @ts-expect-error: strict-null-checks
              setUser(undefined);
              clearCustomerWidgetAuthToken();
              navigate("/returns-portal/login");
              break;
            case MenuOption.MyWarranties:
              navigate("/returns-portal/account/warranties");
              break;
            default:
              assertNever(item.value);
          }
        }}
        setDropdownOpen={setDropdownOpen}
        size="xs"
      />

      <Flex align="center" gap="sm">
        <Flex align="center" dir="row">
          {screen === ScreenType.DESKTOP && (primaryEmail || customerName) && (
            <Flex align="flex-end" dir="column" gap="none">
              {customerName && (
                <Text fontSize="sm" fontWeight="bold">
                  {customerName}
                </Text>
              )}
              {primaryEmail && (
                <Text fontSize="sm" fontWeight="thin">
                  {primaryEmail}
                </Text>
              )}
            </Flex>
          )}
          <Avatar
            alt={primaryEmail || ""}
            imageUrl={
              primaryEmail ? getGravatarUrlFromEmail(primaryEmail) : null
            }
          />
        </Flex>
        <RedoButton
          hierarchy="tertiary"
          IconTrailing={ChevronDownIcon}
          onClick={(event) => {
            setDropdownOpen(!dropdownOpen);
            event.stopPropagation();
          }}
          ref={setDropdownButtonRef}
          size="md"
        />
      </Flex>
    </Flex>
  );
}
